import React, { useCallback, useState } from 'react'
import { Grid, makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import { Link, Redirect, useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import AdminPage from '../../../../admin/shared/AdminPage'
import Loading from '../../../../../molecules/Loading'
import SydButton from '../../../../../commonDesign/Button'
import FlexRow from '../../../../../molecules/FlexRow'
import { useDenaliContext } from '../../../DenaliProvider'
import { decipherError } from '../../../../../../utils/decipherError'
import { useDataMartForm } from './useDataMartForm'
import ConfigurationTab from './ConfigurationTab'
import RunsTab from './RunsTab'
import DataMartStatusBadge from './DataMartStatusBadge'

const sections = [
  { value: 'configuration', label: 'Configuration' },
  { value: 'runs', label: 'Runs' }
]

const useStyles = makeStyles((theme) => ({
  sectionNavItem: {
    paddingLeft: theme.layout.padding.p10,
    paddingRight: theme.layout.padding.p10,
    '&:not(:first-child)': {
      borderLeft: '1px solid var(--color-neutral-700)'
    }
  },
  sectionNavLink: {
    userSelect: 'none',
    textDecoration: 'none',
    color: 'inherit',
    fontWeight: theme.typography.weights.light,
    '&.__active': {
      fontWeight: theme.typography.weights.bold,
      color: 'var(--color-primary-500)'
    }
  }
}))

function EditDataMart () {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const { url } = useRouteMatch()
  const { setAlert } = useDenaliContext()
  const [saveErrors, setSaveErrors] = useState(null)
  const {
    form, isNew, isLoading, isSaving, isReconciling, reconcileResult,
    pipelineList, pipelineDetail,
    setField, setConfig, setTableConfig, setBehavior, setWorkflowType,
    deriveColumnsFromPipeline, resetMigrationHash, save, reconcile,
    WORKFLOW_TYPES, RUN_BY_OPTIONS, PERIOD_OPTIONS, DATA_TYPES
  } = useDataMartForm()

  const handleSave = useCallback(async () => {
    try {
      setSaveErrors(null)
      await save()
      history.push('/admin/denali/analysis/data-marts')
    } catch (err) {
      console.error('Failed to save data mart', err)
      setAlert({ severity: 'error', alertMessage: decipherError(err) })
      const validationErrors = err?.response?.data
      if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        setSaveErrors(validationErrors)
      }
    }
  }, [save, history, setAlert])

  const handleReconcile = useCallback(async () => {
    return reconcile(true)
  }, [reconcile])

  const currentSection = location.pathname.endsWith('/runs') ? 'runs' : 'configuration'

  if (isLoading) {
    return <AdminPage><Loading /></AdminPage>
  }

  if (!isNew && !location.pathname.endsWith('/configuration') && !location.pathname.endsWith('/runs')) {
    return <Redirect to={`${url}/configuration`} />
  }

  return (
    <AdminPage>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FlexRow style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <FlexRow style={{ gap: '12px', alignItems: 'center' }}>
              <h2>{isNew ? 'New Data Mart' : `Data Mart: ${form.name}`}</h2>
              {!isNew && <DataMartStatusBadge timeSeriesId={form.timeSeriesId} variant='full' />}
            </FlexRow>
            <FlexRow style={{ gap: '8px', alignItems: 'center' }}>
              {!isNew && (
                <FlexRow>
                  {sections.map((s) => (
                    <div key={s.value} className={classes.sectionNavItem}>
                      <Link
                        to={`${url}/${s.value}`}
                        replace
                        className={clsx(classes.sectionNavLink, { __active: currentSection === s.value })}
                      >
                        {s.label}
                      </Link>
                    </div>
                  ))}
                </FlexRow>
              )}
              <SydButton
                variant='outline'
                size='sm'
                onClick={() => history.push('/admin/denali/analysis/data-marts')}
              >
                Cancel
              </SydButton>
              <SydButton
                variant='primary'
                size='sm'
                onClick={handleSave}
                disabled={isSaving || !form.name || !form.configuration.sourcePipelineId}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </SydButton>
            </FlexRow>
          </FlexRow>
        </Grid>

        {saveErrors && (
          <Grid item xs={12}>
            <div
              style={{
                padding: '12px',
                border: '1px solid var(--color-danger-500)',
                borderRadius: 'var(--border-radius-sm)',
                backgroundColor: 'color-mix(in srgb, var(--color-danger-100) 20%, transparent)'
              }}
            >
              <div style={{ fontWeight: 600, color: 'var(--color-danger-500)', marginBottom: '6px' }}>
                Validation errors:
              </div>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.8125rem' }}>
                {saveErrors.map((e, i) => (
                  <li key={i} style={{ color: 'var(--color-danger-500)' }}>
                    {e.message}
                  </li>
                ))}
              </ul>
            </div>
          </Grid>
        )}

        <Grid item xs={12}>
          {currentSection === 'configuration' && (
            <ConfigurationTab
              form={form}
              isNew={isNew}
              isSaving={isSaving}
              isReconciling={isReconciling}
              reconcileResult={reconcileResult}
              pipelineList={pipelineList}
              pipelineDetail={pipelineDetail}
              setField={setField}
              setConfig={setConfig}
              setTableConfig={setTableConfig}
              setBehavior={setBehavior}
              setWorkflowType={setWorkflowType}
              deriveColumnsFromPipeline={deriveColumnsFromPipeline}
              resetMigrationHash={resetMigrationHash}
              onSave={handleSave}
              onReconcile={handleReconcile}
              WORKFLOW_TYPES={WORKFLOW_TYPES}
              RUN_BY_OPTIONS={RUN_BY_OPTIONS}
              PERIOD_OPTIONS={PERIOD_OPTIONS}
              DATA_TYPES={DATA_TYPES}
            />
          )}

          {currentSection === 'runs' && !isNew && (
            <RunsTab
              timeSeriesId={form.timeSeriesId}
              enabled={form.enabled}
            />
          )}
        </Grid>
      </Grid>
    </AdminPage>
  )
}

export default EditDataMart
